import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTable } from '../lib/realtime';
import type { Vehicle } from '../lib/types';

export type VehicleWithCompany = Vehicle & {
  company?: { id: string; name: string } | null;
};

export type VehicleInsert = {
  plate: string;
  model: string;
  year?: number | null;
  company_id?: string | null;
  status?: Vehicle['status'];
};

interface UseVehiclesOptions {
  companyId?: string | null;
}

interface UseVehiclesResult {
  vehicles: VehicleWithCompany[];
  loading: boolean;
  error: string | null;
  createVehicle: (data: VehicleInsert) => Promise<{ data: Vehicle | null; error: string | null }>;
  updateVehicle: (id: string, data: Partial<VehicleInsert>) => Promise<{ error: string | null }>;
  toggleStatus: (id: string, current: Vehicle['status']) => Promise<{ error: string | null }>;
  refetch: () => void;
}

export function useVehicles(options: UseVehiclesOptions = {}): UseVehiclesResult {
  const { companyId } = options;
  const [vehicles, setVehicles] = useState<VehicleWithCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    let query = supabase
      .from('vehicles')
      .select('*, company:companies(id, name)')
      .order('plate', { ascending: true });

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error: err } = await query;

    if (err) {
      setError(err.message);
      setVehicles([]);
    } else {
      setVehicles((data as VehicleWithCompany[]) ?? []);
    }
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    refetch();
    const unsub = subscribeTable('vehicles', refetch);
    return unsub;
  }, [refetch]);

  useEffect(() => {
    if (tick === 0) return;
    refetch();
  }, [tick, refetch]);

  async function createVehicle(data: VehicleInsert) {
    const { data: result, error: err } = await supabase
      .from('vehicles')
      .insert(data)
      .select('*')
      .single();

    if (!err) setTick((t) => t + 1);
    return { data: (result as Vehicle | null), error: err?.message ?? null };
  }

  async function updateVehicle(id: string, data: Partial<VehicleInsert>) {
    const { error: err } = await supabase.from('vehicles').update(data).eq('id', id);
    if (!err) setTick((t) => t + 1);
    return { error: err?.message ?? null };
  }

  async function toggleStatus(id: string, current: Vehicle['status']) {
    const next: Vehicle['status'] =
      current === 'active' ? 'inactive' : current === 'inactive' ? 'active' : 'active';
    return updateVehicle(id, { status: next });
  }

  return {
    vehicles,
    loading,
    error,
    createVehicle,
    updateVehicle,
    toggleStatus,
    refetch: () => setTick((t) => t + 1),
  };
}
